import { getAuthToken } from "../context/auth-context";
import { CALL_API } from "../context/constValues";

// ✅ Orden exacto de los 18 accesos según los parámetros IN del SP
// [Grupo][Subacceso] → campo que se manda al backend
const ORDEN_ACCESOS = [
  ["Ventas",   "Clientes",         "ventas_clientes"],
  ["Ventas",   "Facturas",         "ventas_facturas"],
  ["Ventas",   "FichaFactura",     "ventas_fichafactura"],
  ["Ventas",   "Detalles",         "ventas_detalles"],
  ["Ventas",   "Reportes",         "ventas_reportes"],
  ["Ventas",   "FacturasContado",  "ventas_facturascontado"],
  ["Ventas",   "FacturasCredito",  "ventas_facturascredito"],
  ["Ventas",   "Montos",           "ventas_montos"],
  ["Compras",  "Proveedores",      "compras_proveedores"],
  ["Compras",  "Compras",          "compras_compras"],
  ["Compras",  "ListaCompras",     "compras_listacompras"],
  ["Compras",  "Reportes",         "compras_reportes"],
  ["Opciones", "Timbrado",         "opciones_timbrado"],
  ["Opciones", "Sucursal",         "opciones_sucursal"],
  ["Opciones", "PuntosDeVenta",    "opciones_puntosdeventa"],
  ["Opciones", "FormasDePago",     "opciones_formasdepago"],
  ["Opciones", "ServicioProducto", "opciones_servicioproducto"],
  ["Opciones", "Seguros",          "opciones_seguros"],
];

function appendAccesosOrdenados(accesos, fd) {
  ORDEN_ACCESOS.forEach(([grupo, subacceso, campo]) => {
    const valor = accesos?.[grupo]?.[subacceso] ?? false;
    fd.append(campo, valor ? "true" : "false");
  });
}

export async function insertAdmin(objeto) {
  const { cuenta, contraseña, nombre, cedula, foto, accesos } = objeto;

  if (!cuenta || !contraseña || !nombre || !cedula) {
    throw new Error("Los campos cuenta, contraseña, nombre completo y cédula son obligatorios.");
  }

  const token = getAuthToken();
  const formData = new FormData();
  formData.append("cuenta", cuenta);
  formData.append("contrasena", contraseña); // sin tilde para el backend
  formData.append("nombrecompleto", nombre);
  formData.append("cedula", cedula);

  // ✅ 18 accesos en orden garantizado (reemplaza Object.entries que no garantiza orden)
  appendAccesosOrdenados(accesos, formData);

  if (foto instanceof File) {
    const extension = foto.name.split(".").pop();
    const nuevoNombre = `${cedula}.${extension}`;
    formData.append("image", foto, nuevoNombre);
    formData.append("foto", nuevoNombre);
  }

  try {
    const response = await fetch(`${CALL_API}/InsertAdmin`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Error al registrar el usuario");
    }
    return data.isCreated;
  } catch (error) {
    console.error("Error en insertAdmin:", error);
    throw error;
  }
}

export async function updateAdmin(objeto) {
  const { idadmin, cuenta, nombre, cedula, foto, accesos } = objeto;

  if (!idadmin) {
    throw new Error("El ID del administrador es obligatorio.");
  }
  if (!cuenta || !nombre || !cedula) {
    throw new Error("Los campos cuenta, nombre completo y cédula son obligatorios.");
  }

  const token = getAuthToken();
  const formData = new FormData();
  formData.append("cuenta", cuenta);
  formData.append("nombrecompleto", nombre);
  formData.append("cedula", cedula);

  // ✅ 18 accesos en orden garantizado
  appendAccesosOrdenados(accesos, formData);

  if (foto instanceof File) {
    const extension = foto.name.split(".").pop();
    const nuevoNombre = `${cedula}.${extension}`;
    formData.append("image", foto, nuevoNombre);
    formData.append("foto", nuevoNombre);
  } else if (foto) {
    formData.append("foto", foto);
  }

  try {
    const response = await fetch(`${CALL_API}/UpdateAdmin/${idadmin}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Error al actualizar el usuario");
    }
    return data.isSaved;
  } catch (error) {
    console.error("Error en updateAdmin:", error);
    throw error;
  }
}

export async function getAllAdmin() {
  const token = getAuthToken();
  const response = await fetch(`${CALL_API}/admin`, {
    headers: { Authorization: "Bearer " + token },
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error("No se encontro informacion");
  }
  return data.admins;
}

export async function UpdateEstadoAdmin(id) {
  const token = getAuthToken();
  const response = await fetch(`${CALL_API}/updateEstadoAdmin/${id}`, {
    method: "POST",
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    }
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error("No se encontro informacion");
  }
  return data;
}

export async function changePassword(usuarioObject) {
  const { newPassword, currentPassword } = usuarioObject;
  const token = getAuthToken();
  const result = await fetch(`${CALL_API}/resetAdminPass/`, {
    method: "PATCH",
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ newPassword, currentPassword }),
  });
  const data = await result.json();
  if (!result.ok) {
    throw new Error(data.message);
  }
  return data.isReset;
}

export async function loginAdmin(usuarioObject) {
  const { usuario, password } = usuarioObject;
  const response = await fetch(`${CALL_API}/loginAdmin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ usuario, password }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message);
  }
  return data.token;
}

export async function getAdminById(idUsuario) {
  const token = getAuthToken();
  const response = await fetch(`${CALL_API}/admin/${idUsuario}`, {
    headers: { Authorization: "Bearer " + token },
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error("No se encontro informacion");
  }
  return data.result;
}

export async function getAdminCredentials() {
  const token = getAuthToken();
  const response = await fetch(`${CALL_API}/admin-credentials`, {
    headers: { Authorization: "Bearer " + token },
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message);
  }
  return data.userData;
}

export async function changePasswordAdmin(usuarioObject) {
  const { newPassword, idadmin, cuenta } = usuarioObject;
  const token = getAuthToken();
  const result = await fetch(`${CALL_API}/resetAdminPass2/`, {
    method: "PATCH",
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ newPassword, idadmin, usuario: cuenta }),
  });
  const data = await result.json();
  if (!result.ok) {
    throw new Error(data.message);
  }
  return data.isReset;
}

export async function getAllCondiciones() {
  const token = getAuthToken();
  const response = await fetch(`${CALL_API}/condiciones`, {
    headers: { Authorization: "Bearer " + token },
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error("No se encontro informacion");
  }
  return data;
}

export async function insertCondicion(objeto) {
  const { cod, condicion } = objeto;
  if (!cod || !condicion) {
    throw new Error("Los campos código y condición son obligatorios.");
  }
  const token = getAuthToken();
  try {
    const response = await fetch(`${CALL_API}/insertCondicion`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ cod, condicion })
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Error al registrar la condición");
    }
    return data.isSaved;
  } catch (error) {
    console.error("Error en insertCondicion:", error);
    throw error;
  }
}

export async function updateCondicion(objeto) {
  const { idcondiciones, cod, condicion } = objeto;
  if (!idcondiciones || !cod || !condicion) {
    throw new Error("Los campos idcondiciones, código y condición son obligatorios.");
  }
  const token = getAuthToken();
  try {
    const response = await fetch(`${CALL_API}/condiciones/${idcondiciones}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ cod, condicion })
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Error al actualizar la condición");
    }
    return data.isSaved;
  } catch (error) {
    console.error("Error en updateCondicion:", error);
    throw error;
  }
}